import type { AdminBillingOrganization } from '@/api/services/adminBilling';
import type { BillingPlan } from '@/api/services/billingAdmin';
import { findPlanIdForSlug } from '@/utils/billingPlanForm';

type OrgBillingSnapshot = Pick<AdminBillingOrganization, 'entitlements' | 'subscription' | 'health'>;

/** Active entitlement from server list (ordered by starts_at DESC). */
export function resolveActiveEntitlement(
  data: OrgBillingSnapshot | null,
): Record<string, unknown> | null {
  if (!data) return null;
  return (
    data.entitlements.find((item) =>
      ['active', 'manual_grant'].includes(String(item.status)),
    ) ?? null
  );
}

/** Single source of truth for the organization's effective plan slug. */
export function resolveEffectiveOrganizationPlanSlug(data: OrgBillingSnapshot | null): string {
  if (!data) return '';
  const active = resolveActiveEntitlement(data);
  const slug = String(
    active?.planSlug || data.subscription?.planSlug || data.health?.planSlug || '',
  );
  return slug.trim().toLowerCase();
}

export function resolveEffectiveOrganizationPlanId(
  plans: BillingPlan[],
  data: OrgBillingSnapshot | null,
): string {
  return findPlanIdForSlug(plans, resolveEffectiveOrganizationPlanSlug(data));
}

/** Optimistically merge a successful grant response into org billing detail. */
export function applyGrantToOrganizationBilling(
  prev: AdminBillingOrganization,
  granted: Record<string, unknown>,
  plans: BillingPlan[],
): AdminBillingOrganization {
  const planSlug = String(granted.planSlug || '').trim().toLowerCase();
  const planName =
    String(granted.planName || '') ||
    plans.find((plan) => plan.slug === planSlug)?.name ||
    planSlug;
  const newEntitlement = {
    ...granted,
    planSlug,
    planName,
    status: granted.status || 'manual_grant',
  };
  const entitlements = [
    newEntitlement,
    ...prev.entitlements.map((item) =>
      ['active', 'manual_grant'].includes(String(item.status))
        ? { ...item, status: 'superseded' }
        : item,
    ),
  ];
  return {
    ...prev,
    subscription: { ...prev.subscription, planSlug },
    health: { ...prev.health, planSlug },
    entitlements,
  };
}
